import { useQueryClient } from 'react-query';

import { useApiMutation, useApiQuery } from '../../api/useApi';
import type { InboxAssignee, InboxStatus, SupportSchedule } from '../../api/superAdmin/support/types';
import { useSupportInbox, useSupportSummary } from '../../api/superAdmin/support/useSupportAdmin';
import { usePermissions } from '../../contexts/PermissionContext';

/**
 * Le support de l'équipe TriggerFlow vu du mobile : les MÊMES endpoints et hooks
 * que le site (`/super-admin/support/...`), aucune API dédiée. Ce fichier ne fait
 * que composer, plus « Mes horaires » (`/super-admin/support/my-schedule`).
 *
 * Les hooks du site tournent sous le `QueryClient` mobile (`queryClientMobile`) :
 * ils ne dépendent que de `useApiQuery` / `useApiMutation` / `useQueryClient`.
 */
export {
    useCloseSupportConversation,
    useDiscardSupportDraft,
    useReplySupportConversation,
    useResendSupportMessage,
    useSupportConversation,
} from '../../api/superAdmin/support/useSupportAdmin';
export { useAddInternalNote, useAssignConversation } from '../../api/superAdmin/support/useSupportQueue';
export { useConversationGmail, useSupportGmailLabels, useSupportGmailStatus } from '../../api/superAdmin/support/useSupportGmail';
export { useSavedReplies } from '../../api/superAdmin/support/useSupportSavedReplies';
export { useDraftFromTeamAnswer, useRateTeamAnswer, useSendTeamChat } from '../../api/superAdmin/support/useSupportTeamAssist';
export { useConversationAssist } from '../../api/superAdmin/support/useConversationAssist';
export type { ConversationAssist } from '../../api/superAdmin/support/useConversationAssist';
export { useFacilitySupportNotes } from '../../api/superAdmin/support/useFacilitySupportNotes';
export { useCrmFacility } from '../../api/useCrm';
export { useEmailThread, useEmailThreadBrowser } from '../../api/superAdmin/support/useSupportEmailThreads';

export type FiltreSupport = 'ouvertes' | 'a_traiter' | 'a_moi' | 'en_cours' | 'toutes';

/**
 * « Ouvertes » = toutes sauf les closes, celles qui attendent l'équipe d'abord ; « À traiter » = escaladées, personne n'a encore répondu ; « À moi » = attribuées à
 * l'agent connecté, hors fermées (une conversation fermée garde son attribution) ; « En attente » = prises en charge par l'équipe, la
 * balle est chez l'hôtelier ; « En attente client » = mises en attente d'un retour de
 * l'hôtelier (fermeture automatique sans réponse) ; « Toutes » = sans filtre.
 * La liste « À traiter » filtre sur `waiting_human` ; son compteur, lui, lit `to_handle`
 * (brouillons à relire inclus).
 */
const FILTRES: Record<FiltreSupport, { statut: InboxStatus | 'open' | 'all'; attribution: InboxAssignee }> = {
    // File par défaut : tout sauf les closes, celles qui attendent l'équipe d'abord (tri du serveur).
    ouvertes: { statut: 'open', attribution: 'all' },
    a_traiter: { statut: 'waiting_human', attribution: 'all' },
    a_moi: { statut: 'open', attribution: 'me' },
    en_cours: { statut: 'human', attribution: 'all' },
    toutes: { statut: 'all', attribution: 'all' },
};

export function useFileSupport(filtre: FiltreSupport, page: number) {
    const { statut, attribution } = FILTRES[filtre];
    return useSupportInbox(statut, page, 'all', attribution);
}

/** Même rythme que le compteur de la messagerie (30 s). */
const SONDAGE_SUPPORT_MS = 30_000;

/**
 * Ce que l'équipe a à traiter : `to_handle` du résumé, compté côté serveur sur des
 * conversations distinctes (en attente d'un humain OU brouillon IA à relire), le même
 * total que le badge posé par le push (`SupportQueue::toHandleCount`). Pas de somme
 * côté client : une conversation dans les deux cas serait comptée deux fois.
 *
 * 0 et AUCUNE requête hors super admin : l'endpoint répond 403 à tout autre compte.
 */
export function useNombreSupportATraiter(): number {
    const { isSuperAdmin } = usePermissions();
    const actif = isSuperAdmin === true;
    const { data } = useSupportSummary(actif, actif ? SONDAGE_SUPPORT_MS : false);

    if (!actif || !data) return 0;
    return data.to_handle ?? 0;
}

export interface MesHorairesSupport {
    support_schedule: SupportSchedule | null;
    support_absent: boolean;
    on_duty_now: boolean;
}

const URL_MES_HORAIRES = '/super-admin/support/my-schedule';

export function useMesHoraires() {
    return useApiQuery<MesHorairesSupport>(URL_MES_HORAIRES);
}

export function useEnregistrerMesHoraires() {
    const queryClient = useQueryClient();
    return useApiMutation<MesHorairesSupport, unknown, Omit<MesHorairesSupport, 'on_duty_now'>>('put', () => URL_MES_HORAIRES, {
        onSuccess: (data) => {
            queryClient.setQueryData([URL_MES_HORAIRES, {}], data);
        },
    });
}
